// Chakra Imports
import {
	Avatar,
	Box,
	Button,
	Flex,
	Icon,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	Portal,
	Text,
	useColorModeValue,
	useColorMode
} from '@chakra-ui/react';
// Custom Components
import { ItemContent } from 'components/menu/ItemContent';
import { PatientSearchBar } from 'components/navbar/searchBar/PatientSearchBar';
import { Dropdown } from 'components/ui/Dropdown';
import { NewPatientDrawer } from 'components/patients/NewPatientDrawer';
import { NewEpisodeDrawer } from 'components/episodes/NewEpisodeDrawer';
import AppointmentDrawer from 'components/layouts/patients/AppointmentDrawer';
import { SidebarResponsive } from 'components/sidebar/Sidebar';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useClerk, useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
// Assets
import { MdNotificationsNone, MdCalendarToday } from 'react-icons/md';
import { IoMdMoon, IoMdSunny } from 'react-icons/io';
import routes from 'routes';
import TodayQuickDrawer from 'components/layouts/dashboard/TodayQuickDrawer';
import { useAppointments } from 'hooks/useAppointments';
import { useCurrentDbUser } from 'hooks/useCurrentUser';
import { EstadoCita } from 'types/models';
import { useMemo } from 'react';
export default function HeaderLinks(props: { secondary: boolean }) {
	const { secondary } = props;
	const { colorMode, toggleColorMode } = useColorMode();
	const { signOut } = useClerk();
	const { user } = useUser();
	const navigate = useNavigate();

	const handleSignOut = () => signOut(() => navigate('/auth/sign-in', { replace: true }));
	const [isNewPatientOpen,     setIsNewPatientOpen]     = useState(false);
	const [isNewEpisodeOpen,     setIsNewEpisodeOpen]     = useState(false);
	const [isTodayOpen,          setIsTodayOpen]          = useState(false);
	const [isNewAppointmentOpen, setIsNewAppointmentOpen] = useState(false);

	const { data: me } = useCurrentDbUser();
	const todayStart = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d.toISOString(); }, []);
	const todayEnd   = useMemo(() => { const d = new Date(); d.setHours(23,59,59,999); return d.toISOString(); }, []);
	const { data: todayData } = useAppointments(
		me?.id ? { desde: todayStart, hasta: todayEnd, professionalId: me.id, limit: 50 } : {}
	);
	const pendientesHoy = useMemo(
		() => (todayData?.data ?? []).filter(a => a.estado === EstadoCita.CONFIRMADA).length,
		[todayData]
	);
	// Chakra Color Mode
	const navbarIcon = useColorModeValue('gray.400', 'white');
	let menuBg = useColorModeValue('white', 'navy.800');
	const textColor = useColorModeValue('secondaryGray.900', 'white');
	const textColorBrand = useColorModeValue('brand.700', 'brand.400');
	const borderColor = useColorModeValue('#E6ECFA', 'rgba(135, 140, 189, 0.3)');
	const navbarIconHover = useColorModeValue('gray.600', 'gray.100');
	const shadow = useColorModeValue(
		'14px 17px 40px 4px rgba(112, 144, 176, 0.18)',
		'14px 17px 40px 4px rgba(112, 144, 176, 0.06)'
	);
	return (
		<>
		<Flex
			w={{ sm: '100%', md: 'auto' }}
			alignItems='center'
			flexDirection='row'
			bg={menuBg}
			flexWrap={secondary ? { base: 'wrap', md: 'nowrap' } : 'unset'}
			p='10px'
			borderRadius='30px'
			boxShadow={shadow}>
			<PatientSearchBar
				mb={secondary ? { base: '10px', md: 'unset' } : 'unset'}
				ms='8px'
				me='15px'
				h='30px'
				ps='36px'
				pe='12px'
			/>
			
			<SidebarResponsive routes={routes} />

			{/* Citas de hoy */}
			<Box position='relative' me='10px' cursor='pointer' onClick={() => setIsTodayOpen(true)}>
				<Icon as={MdCalendarToday} color={navbarIcon} w='21px' h='21px' verticalAlign='middle'
					_hover={{ color: navbarIconHover }} transition='color 0.15s' />
				{pendientesHoy > 0 && (
					<Box
						position='absolute'
						top='-5px'
						right='-6px'
						bg='red.500'
						color='white'
						borderRadius='full'
						minW='16px'
						h='16px'
						display='flex'
						alignItems='center'
						justifyContent='center'
						fontSize='9px'
						fontWeight='700'
						px='3px'
						lineHeight='1'
					>
						{pendientesHoy > 9 ? '9+' : pendientesHoy}
					</Box>
				)}
			</Box>

			<Menu>
				<MenuButton p='0px'>
					<Icon as={MdNotificationsNone} color={navbarIcon} w='21px' h='21px' me='10px' verticalAlign='middle' />
				</MenuButton>
				<MenuList
					boxShadow={shadow}
					p='20px'
					borderRadius='20px'
					bg={menuBg}
					border='none'
					mt='22px'
					me={{ base: '30px', md: 'unset' }}
					minW={{ base: 'unset', md: '400px', xl: '450px' }}
					maxW={{ base: '360px', md: 'unset' }}>
					<Flex w='100%' mb='20px'>
						<Text fontSize='md' fontWeight='600' color={textColor}>
							Notifications
						</Text>
						<Text fontSize='sm' fontWeight='500' color={textColorBrand} ms='auto' cursor='pointer'>
							Mark all read
						</Text>
					</Flex>
					<Flex flexDirection='column'>
						<MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} px='0' borderRadius='8px' mb='10px'>
							<ItemContent info='Horizon UI Dashboard PRO' />
						</MenuItem>
						<MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} px='0' borderRadius='8px' mb='10px'>
							<ItemContent info='Horizon Design System Free' />
						</MenuItem>
					</Flex>
				</MenuList>
			</Menu>

			<Dropdown
				me='10px'
				itemColor={textColor}
				itemHoverColor={navbarIconHover}
				items={[
					{
						label: 'Nuevo Paciente',
						value: 'nuevo-paciente',
						onClick: () => setIsNewPatientOpen(true),
						icon: (
							<Icon viewBox='0 0 24 24' w='16px' h='16px' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
								<path d='M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2' />
								<circle cx='12' cy='7' r='4' />
							</Icon>
						),
					},
					{
						label: 'Nuevo Episodio',
						value: 'nuevo-episodio',
						onClick: () => setIsNewEpisodeOpen(true),
						icon: (
							<Icon viewBox='0 0 24 24' w='16px' h='16px' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
								<rect width='8' height='4' x='8' y='2' rx='1' ry='1' />
								<path d='M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' />
								<path d='M9 14h6' />
								<path d='M12 17v-6' />
							</Icon>
						),
					},
					{
						label: 'Crear Cita',
						value: 'crear-cita',
						onClick: () => setIsNewAppointmentOpen(true),
						icon: (
							<Icon viewBox='0 0 24 24' w='16px' h='16px' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
								<path d='M16 19h6' />
								<path d='M16 2v4' />
								<path d='M19 16v6' />
								<path d='M21 12.598V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8.5' />
								<path d='M3 10h18' />
								<path d='M8 2v4' />
							</Icon>
						),
					},
				]}
				trigger={
					<Box
						display='flex'
						alignItems='center'
						gap='3px'
						borderRadius='5px'
						border='1px solid'
						borderColor={borderColor}
						px='5px'
						py='5px'
						h='28px'
					>
						<Icon
							color={navbarIcon}
							w='16px'
							h='16px'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
							verticalAlign='middle'
						>
							<path d='M5 12h14' />
							<path d='M12 5v14' />
						</Icon>
						<Icon
							color={navbarIcon}
							w='14px'
							h='14px'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
							verticalAlign='middle'
						>
							<path d='m6 9 6 6 6-6' />
						</Icon>
					</Box>
				}
				menuWidth='170px'
			/>

			<Button
				variant='no-hover'
				bg='transparent'
				p='0px'
				minW='unset'
				minH='unset'
				h='18px'
				w='max-content'
				onClick={toggleColorMode}>
				<Icon
					me='10px'
					h='18px'
					w='18px'
					color={navbarIcon}
					as={colorMode === 'light' ? IoMdMoon : IoMdSunny}
				/>
			</Button>
			<Menu placement='bottom-end'>
				<MenuButton p='0px'>
					<Avatar
						_hover={{ cursor: 'pointer' }}
						color='white'
						name={user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : ''}
						src={user?.imageUrl}
						bg='#11047A'
						size='sm'
						w='40px'
						h='40px'
					/>
				</MenuButton>
				<Portal>
					<MenuList boxShadow={shadow} p='0px' mt='10px' borderRadius='20px' bg={menuBg} border='none' zIndex='1500' minW='220px'>
						<Flex w='100%' mb='0px'>
							<Text
								ps='20px'
								pt='16px'
								pb='10px'
								w='100%'
								borderBottom='1px solid'
								borderColor={borderColor}
								fontSize='sm'
								fontWeight='700'
								color={textColor}>
								👋&nbsp; Hey, {user?.firstName ?? 'Usuario'}
							</Text>
						</Flex>
						<Flex flexDirection='column' p='10px'>
							<MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} borderRadius='8px' px='14px'>
								<Text fontSize='sm'>Profile Settings</Text>
							</MenuItem>
							<MenuItem _hover={{ bg: 'none' }} _focus={{ bg: 'none' }} borderRadius='8px' px='14px'>
								<Text fontSize='sm'>Newsletter Settings</Text>
							</MenuItem>
							<MenuItem
								_hover={{ bg: 'none' }}
								_focus={{ bg: 'none' }}
								color='red.400'
								borderRadius='8px'
								px='14px'
								onClick={handleSignOut}>
								<Text fontSize='sm'>Cerrar sesión</Text>
							</MenuItem>
						</Flex>
					</MenuList>
				</Portal>
			</Menu>
		</Flex>

		<NewPatientDrawer
			isOpen={isNewPatientOpen}
			onClose={() => setIsNewPatientOpen(false)}
		/>
		<NewEpisodeDrawer
			isOpen={isNewEpisodeOpen}
			onClose={() => setIsNewEpisodeOpen(false)}
		/>
		<TodayQuickDrawer
			isOpen={isTodayOpen}
			onClose={() => setIsTodayOpen(false)}
		/>
		<AppointmentDrawer
			isOpen={isNewAppointmentOpen}
			onClose={() => setIsNewAppointmentOpen(false)}
			professionalId={me?.id ?? ''}
			onSuccess={() => setIsNewAppointmentOpen(false)}
		/>
		</>
	);
}

HeaderLinks.propTypes = {
	variant: PropTypes.string,
	fixed: PropTypes.bool,
	secondary: PropTypes.bool,
	onOpen: PropTypes.func
};
